import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, clearAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, Users, BarChart3, LogOut, Plus, MapPin, DollarSign, Car, Trash2 } from 'lucide-react';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [societies, setSocieties] = useState([]);
  const [showCreateSociety, setShowCreateSociety] = useState(false);
  const [showCreateAdmin, setShowCreateAdmin] = useState(null);
  const [societyForm, setSocietyForm] = useState({ name: '', address: '' });
  const [adminForm, setAdminForm] = useState({ phone: '', name: '', password: '' });
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, societiesRes] = await Promise.all([
        axios.get(`${API}/superadmin/stats`),
        axios.get(`${API}/superadmin/societies`)
      ]);
      setStats(statsRes.data);
      setSocieties(societiesRes.data.societies || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('role');
    const role = localStorage.getItem('role');
    if (!role || role !== 'superadmin') {
      navigate('/auth/admin');
      return;
    }
    fetchData();
  }, [navigate, fetchData]);

  const handleCreateSociety = async () => {
    if (!societyForm.name.trim()) { toast.error('Society name required'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/superadmin/societies`, societyForm);
      toast.success('Society created');
      setSocietyForm({ name: '', address: '' });
      setShowCreateSociety(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create society');
    } finally { setLoading(false); }
  };

  const handleCreateAdmin = async (societyId) => {
    if (!adminForm.phone || !adminForm.name || !adminForm.password) {
      toast.error('All fields required');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/superadmin/society-admin`, { ...adminForm, society_id: societyId });
      toast.success('Society admin created');
      setAdminForm({ phone: '', name: '', password: '' });
      setShowCreateAdmin(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally { setLoading(false); }
  };

  const handleDeleteSociety = async (societyId, societyName) => {
    if (!window.confirm(`Are you sure you want to delete "${societyName}"?\n\nThis will permanently remove all buildings, flats, parking spots, users, and bookings for this society.`)) return;
    try {
      const res = await axios.delete(`${API}/superadmin/societies/${societyId}`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete society');
    }
  };

  const handleLogout = () => { axios.post(`${API}/auth/logout`).catch(() => {}); clearAuthToken(); localStorage.clear(); navigate('/'); };

  if (!stats) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F4F4F5' }}>
      <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Loading...</p>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F4F4F5' }}>
      <div className="border-b" style={{ backgroundColor: '#09090B', borderColor: '#27272A' }}>
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div style={{ backgroundColor: '#FBBF24', padding: '12px' }}>
              <Building2 size={28} style={{ color: '#09090B' }} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl tracking-tight font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#FFFFFF' }} data-testid="superadmin-title">
                ParkShare Platform
              </h1>
              <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#A1A1AA', fontSize: '14px' }}>
                Super Admin Console
              </p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" data-testid="logout-button" style={{ fontFamily: 'IBM Plex Sans, sans-serif', borderColor: '#3F3F46', color: '#FFFFFF' }}>
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Societies', value: stats.total_societies, icon: Building2, bg: '#FBBF24' },
            { label: 'Total Owners', value: stats.total_owners, icon: Users, bg: '#10B981' },
            { label: 'Total Revenue', value: `\u20B9${stats.total_revenue.toFixed(0)}`, icon: DollarSign, bg: '#09090B' },
            { label: 'Total Spots', value: stats.total_spots, icon: Car, bg: '#3B82F6' }
          ].map((card) => (
            <div key={card.label} className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }} data-testid={`stat-${card.label.toLowerCase().replace(' ', '-')}`}>
              <div className="flex items-center gap-3 mb-3">
                <div style={{ backgroundColor: card.bg, padding: '10px' }}>
                  <card.icon size={20} style={{ color: card.bg === '#09090B' ? '#FFFFFF' : '#09090B' }} />
                </div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: '#71717A' }}>{card.label}</p>
              </div>
              <p className="text-4xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{card.value}</p>
            </div>
          ))}
        </div>

        {/* Societies Section */}
        <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
              Onboarded Societies
            </h2>
            <Button onClick={() => setShowCreateSociety(!showCreateSociety)} data-testid="create-society-btn" style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}>
              <Plus size={16} className="mr-2" /> Add Society
            </Button>
          </div>

          {/* Create Society Form */}
          {showCreateSociety && (
            <div className="mb-6 p-5 border" style={{ backgroundColor: '#F4F4F5', borderColor: '#E4E4E7' }} data-testid="create-society-form">
              <p className="text-sm font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>New Society</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Society Name</Label>
                  <Input value={societyForm.name} onChange={(e) => setSocietyForm({ ...societyForm, name: e.target.value })} placeholder="e.g., Green Valley Residency" data-testid="society-name-input" className="mt-1" />
                </div>
                <div>
                  <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Address</Label>
                  <Input value={societyForm.address} onChange={(e) => setSocietyForm({ ...societyForm, address: e.target.value })} placeholder="e.g., Sector 15, Noida" data-testid="society-address-input" className="mt-1" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateSociety} disabled={loading} data-testid="save-society-btn" style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}>
                  {loading ? 'Creating...' : 'Create Society'}
                </Button>
                <Button variant="outline" onClick={() => setShowCreateSociety(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Societies List */}
          {societies.length === 0 ? (
            <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>No societies onboarded yet. Click "Add Society" to start.</p>
          ) : (
            <div className="space-y-4">
              {societies.map((society) => (
                <div key={society.id} className="border p-5" style={{ borderColor: '#E4E4E7' }} data-testid={`society-card-${society.code}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <MapPin size={18} style={{ color: '#FBBF24' }} />
                        <h3 className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>{society.name}</h3>
                        <span className="text-xs px-2 py-0.5" style={{ backgroundColor: '#10B981', color: '#FFFFFF', fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          {society.status}
                        </span>
                      </div>
                      {society.address && (
                        <p className="text-sm ml-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>{society.address}</p>
                      )}
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <p className="text-xs" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#71717A' }}>Code: {society.code}</p>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteSociety(society.id, society.name)} data-testid={`delete-society-${society.code}`} className="h-7" style={{ borderColor: '#DC2626', color: '#DC2626' }}>
                        <Trash2 size={12} className="mr-1" /> Remove
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-6 ml-8 mb-3">
                    <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                      <strong style={{ color: '#09090B' }}>{society.buildings_count}</strong> Buildings
                    </span>
                    <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                      <strong style={{ color: '#09090B' }}>{society.flats_count}</strong> Flats
                    </span>
                    <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                      <strong style={{ color: '#09090B' }}>{society.spots_count}</strong> Parking Spots
                    </span>
                  </div>

                  {/* Admin Info */}
                  <div className="ml-8 flex items-center justify-between">
                    {society.admin ? (
                      <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                        Admin: <strong style={{ color: '#09090B' }}>{society.admin.name}</strong> ({society.admin.phone})
                      </p>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#DC2626' }}>No admin assigned</p>
                        <Button size="sm" variant="outline" onClick={() => setShowCreateAdmin(society.id)} data-testid={`assign-admin-${society.code}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>
                          <Plus size={12} className="mr-1" /> Assign Admin
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Create Admin Form */}
                  {showCreateAdmin === society.id && (
                    <div className="mt-4 ml-8 p-4 border" style={{ backgroundColor: '#FFFBEB', borderColor: '#F59E0B' }} data-testid={`admin-form-${society.code}`}>
                      <p className="text-sm font-bold mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#92400E' }}>Create Society Admin</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                        <div>
                          <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>Phone</Label>
                          <Input value={adminForm.phone} onChange={(e) => setAdminForm({ ...adminForm, phone: e.target.value })} placeholder="9876543210" data-testid="admin-phone-input" className="mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                        </div>
                        <div>
                          <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>Name</Label>
                          <Input value={adminForm.name} onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} placeholder="Admin Name" data-testid="admin-name-input" className="mt-1" />
                        </div>
                        <div>
                          <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>Password</Label>
                          <Input type="password" value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} placeholder="Password" data-testid="admin-password-input" className="mt-1" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleCreateAdmin(society.id)} disabled={loading} data-testid="save-admin-btn" style={{ backgroundColor: '#F59E0B', color: '#09090B', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}>
                          {loading ? 'Creating...' : 'Create Admin'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowCreateAdmin(null)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
